import {
  collectAssemblyDocumentNodeIdentityLookupIds,
  listAssemblyDocumentDirectChildNodes,
  resolveAssemblyDocumentNodeOccurrenceAssemblyNodeId,
  resolveProjectModelNodeAssemblyNodeId,
  resolveRepositoryPuppyNodeOccurrenceIdentity,
  type AssemblyDocument,
  type AssemblyDocumentNode,
  type GlbHierarchyNode,
  type ModelAssetNode,
} from '@puppycad/cad-core';
import type { AssemblyBranchNodeHoverReference } from './resolveAssemblyBranchNodeHoverTarget';

/**
 * 左侧装配节点引用识别结果类型。
 */
export type AssemblyBranchNodeReferenceTargetKind =
  | 'project-model-child'
  | 'runtime-embedded-child'
  | 'working-document-child';

/**
 * 左侧装配节点引用识别输入。
 */
export interface ResolveAssemblyBranchNodeReferenceTargetInput {
  /** 当前项目模型根节点。 */
  readonly root: ModelAssetNode;
  /** 当前根装配工作文档；缺失时只能识别 project-model 子树。 */
  readonly assemblyDocument: AssemblyDocument | null;
  /** 当前项目模型根节点 key。 */
  readonly rootNodeKey: string;
  /** 原始节点 `node_where` 所属的仓库 Puppy 包路径。 */
  readonly repositoryPuppyPackageFileId?: string;
  /** 当前 root tab hierarchy；root `.puppy` 加载后用于识别离线包内嵌 direct child。 */
  readonly hierarchy: GlbHierarchyNode | null;
  /** 从当前活动 WorkAssembly snapshot 按稳定 nodeKey 查询运行时组件。 */
  readonly resolveRuntimeBindingByNodeKey?: (
    nodeKey: string,
  ) => PuppyCAD.ProjectModelRuntimeBinding | null;
  /** 左侧分支节点解码出的业务引用。 */
  readonly reference: AssemblyBranchNodeHoverReference;
}

/**
 * 左侧装配节点引用识别结果。
 */
export interface AssemblyBranchNodeReferenceTarget {
  /** 命中来源。 */
  readonly kind: AssemblyBranchNodeReferenceTargetKind;
  /** 当前装配 direct child 的稳定结构 key。 */
  readonly nodeKey: string;
  /** workAssembly 已统一解析的 occurrence 装配身份。 */
  readonly assemblyNodeId: string;
  /** 当前节点仓库 ID。 */
  readonly repositoryId: string;
  /** 当前节点类型。 */
  readonly nodeType: ModelAssetNode['nodeType'];
  /** 当前模型树中命中的一级子节点；仅 `project-model-child` 一定存在。 */
  readonly modelNode: ModelAssetNode | null;
  /** 工作装配文档中命中的 direct child。 */
  readonly documentNode: AssemblyDocumentNode | null;
  /** root `.puppy` hierarchy 内命中的组件节点。 */
  readonly hierarchyNode: GlbHierarchyNode | null;
  /** Viewer 可消费的运行期对象 ID；仅离线包内嵌节点通常存在。 */
  readonly objectId: string | null;
  /** 是否需要走 appendMissingFirstLevelNodeVersion。 */
  readonly shouldAppendMissingNode: boolean;
}

/**
 * 判断装配文档节点是否匹配左侧业务引用。
 *
 * @param node 装配文档 direct child。
 * @param reference 左侧节点业务引用。
 * @returns 是否属于同一个装配实例。
 */
function isDocumentChildMatchedByReference(
  node: AssemblyDocumentNode,
  reference: AssemblyBranchNodeHoverReference,
): boolean {
  if (node.repositoryId.trim() !== reference.repositoryId) {
    return false;
  }

  const instanceUuid = reference.instanceUuid?.trim();
  if (instanceUuid && node.instanceId?.trim() !== instanceUuid) {
    return false;
  }

  const adoptedCommitId = reference.adoptedCommitId?.trim();
  if (!instanceUuid && adoptedCommitId && node.commitId.trim() !== adoptedCommitId) {
    return false;
  }

  return true;
}

/**
 * 在工作装配文档中定位 direct child。
 *
 * @param document 当前根装配文档。
 * @param rootNodeKey 当前根节点 key。
 * @param reference 左侧节点业务引用。
 * @returns 命中的 direct child；无法唯一定位时返回 null。
 */
function resolveDocumentDirectChildByReference(
  document: AssemblyDocument,
  rootNodeKey: string,
  reference: AssemblyBranchNodeHoverReference,
): AssemblyDocumentNode | null {
  const children = listAssemblyDocumentDirectChildNodes(document, rootNodeKey);
  const nodeKey = reference.nodeKey?.trim();

  if (nodeKey) {
    const matchedByNodeKey = children.find((child) => child.nodeKey === nodeKey);
    if (matchedByNodeKey) {
      return matchedByNodeKey;
    }
  }

  const instanceUuid = reference.instanceUuid?.trim();

  if (instanceUuid) {
    return children.find((child) => isDocumentChildMatchedByReference(child, reference)) ?? null;
  }

  const repositoryCandidates = children.filter((child) => child.repositoryId.trim() === reference.repositoryId);
  if (repositoryCandidates.length === 1) {
    return repositoryCandidates[0]!;
  }

  const adoptedCommitId = reference.adoptedCommitId?.trim();
  if (adoptedCommitId) {
    return repositoryCandidates.find((child) => child.commitId.trim() === adoptedCommitId) ?? null;
  }

  return null;
}

/**
 * 按左侧业务引用在 project-model root.children 中定位一级子节点。
 *
 * @param root 当前项目模型根节点。
 * @param reference 左侧节点业务引用。
 * @returns 命中的模型子节点；无法唯一定位时返回 null。
 */
function resolveProjectModelChildByReference(
  root: ModelAssetNode,
  reference: AssemblyBranchNodeHoverReference,
): ModelAssetNode | null {
  const instanceUuid = reference.instanceUuid?.trim();
  if (instanceUuid) {
    const exactInstance = root.children.find((child) =>
      child.repositoryId === reference.repositoryId && child.instanceUuid === instanceUuid);
    if (exactInstance) {
      return exactInstance;
    }
  }

  const candidates = root.children.filter((child) => child.repositoryId === reference.repositoryId);
  if (candidates.length === 1) {
    return candidates[0]!;
  }

  const adoptedCommitId = reference.adoptedCommitId?.trim();
  if (adoptedCommitId) {
    return candidates.find((child) => child.commitId === adoptedCommitId) ?? null;
  }

  return null;
}

/**
 * 通过工作文档 direct child 在 project-model root.children 中定位同一结构节点。
 *
 * 设计说明：root `.puppy` 切换版本曾经会把新版本子节点补入 project-model，
 * 但补入节点的后端 instanceUuid 可能与装配文档 instance_id 不一致。后续再次切换时，
 * 只按 instanceUuid 会误判为缺失并重复 append；nodeKey 才是本地结构替换的稳定锚点。
 *
 * @param root 当前项目模型根节点。
 * @param documentNode 工作文档 direct child。
 * @returns 命中的模型子节点。
 */
function resolveProjectModelChildByDocumentNode(
  root: ModelAssetNode,
  documentNode: AssemblyDocumentNode,
): ModelAssetNode | null {
  const byNodeKey = root.children.find((child) => child.nodeKey === documentNode.nodeKey);
  if (byNodeKey) {
    return byNodeKey;
  }

  const instanceId = documentNode.instanceId?.trim();
  if (instanceId) {
    return root.children.find((child) => child.instanceUuid?.trim() === instanceId) ?? null;
  }

  return null;
}

/**
 * 判断 hierarchy 节点是否是装配/零件组件节点。
 *
 * @param node hierarchy 节点。
 * @returns 是否适合作为装配节点引用锚点。
 */
function isComponentHierarchyNode(node: GlbHierarchyNode): boolean {
  const semanticKind = node.meta?.semanticKind;
  return semanticKind === 'assembly' || semanticKind === 'part';
}

/**
 * 判断 hierarchy 装配节点 ID 是否落在目标 direct child 子树内。
 *
 * @param assemblyNodeId hierarchy 中记录的装配节点 ID。
 * @param candidateIds 工作装配文档 direct child 的稳定身份集合。
 * @returns 当前节点是否可证明属于该 direct child。
 */
function isHierarchyAssemblyNodeIdInCandidateSubtree(
  assemblyNodeId: string,
  candidateIds: ReadonlySet<string>,
): boolean {
  const normalizedAssemblyNodeId = assemblyNodeId.trim();
  if (!normalizedAssemblyNodeId) {
    return false;
  }
  if (candidateIds.has(normalizedAssemblyNodeId)) {
    return true;
  }

  for (const candidateId of candidateIds) {
    const normalizedCandidateId = candidateId.trim();
    if (
      normalizedCandidateId
      && normalizedAssemblyNodeId.startsWith(`${normalizedCandidateId}/`)
    ) {
      return true;
    }
  }

  return false;
}

/**
 * 按稳定 assembly node ID 查找 hierarchy 节点。
 *
 * @param root hierarchy 根节点。
 * @param candidateIds direct child 推导出的候选装配节点 ID。
 * @returns 命中的组件 hierarchy；仅命中实体子节点时返回兜底节点。
 */
function findHierarchyNodeByAssemblyNodeId(
  root: GlbHierarchyNode,
  candidateIds: ReadonlySet<string>,
): GlbHierarchyNode | null {
  const exactComponentMatches: GlbHierarchyNode[] = [];
  const descendantComponentMatches: GlbHierarchyNode[] = [];
  let fallback: GlbHierarchyNode | null = null;

  const visit = (node: GlbHierarchyNode): void => {
    const assemblyNodeId = node.meta?.assemblyNodeId?.trim();
    if (assemblyNodeId && isHierarchyAssemblyNodeIdInCandidateSubtree(assemblyNodeId, candidateIds)) {
      if (isComponentHierarchyNode(node)) {
        if (candidateIds.has(assemblyNodeId)) {
          exactComponentMatches.push(node);
        } else {
          descendantComponentMatches.push(node);
        }
      } else {
        fallback ??= node;
      }
    }

    for (const child of node.children) {
      visit(child);
    }
  };

  visit(root);
  const componentMatches = exactComponentMatches.length > 0
    ? exactComponentMatches
    : descendantComponentMatches;
  if (componentMatches.length > 1) {
    throw new Error(
      'hierarchy occurrence 身份不唯一：'
      + `assemblyNodeIds=${[...candidateIds].join(',')}, `
      + `objectIds=${componentMatches.map(node => node.objectId).join(',')}`,
    );
  }

  return componentMatches[0] ?? fallback;
}

/**
 * 校验异步准备期间 append 目标没有被其它加载事务改写。
 *
 * @param initial 开始 reload 前解析出的 append 目标。
 * @param current reload 完成后从当前项目状态重新解析的目标。
 * @throws occurrence 身份、结构槽位或目标来源变化时抛出并要求调用方重试。
 */
export function assertAssemblyBranchNodeReferenceTargetStableForAppend(
  initial: AssemblyBranchNodeReferenceTarget,
  current: AssemblyBranchNodeReferenceTarget | null,
): void {
  const isStable = current !== null
    && current.assemblyNodeId === initial.assemblyNodeId
    && current.nodeKey === initial.nodeKey
    && current.kind === initial.kind;
  if (isStable) {
    return;
  }

  // append 使用开始阶段捕获的项目快照；目标发生变化时必须中止，避免覆盖新状态或重复插入。
  throw new Error(
    '装配节点状态已变化，请重试：'
    + `assemblyNodeId=${initial.assemblyNodeId}, nodeKey=${initial.nodeKey}, `
    + `initialKind=${initial.kind}, currentKind=${current?.kind ?? '<missing>'}`,
  );
}

/**
 * 解析 hierarchy 节点可交给 Viewer 高亮的对象 ID。
 *
 * @param node 命中的 hierarchy 节点。
 * @returns selectionObjectId 或节点自身 objectId。
 */
function resolveHierarchyObjectId(node: GlbHierarchyNode): string {
  return node.meta?.selectionObjectId?.trim() || node.objectId.trim();
}

/**
 * 解析版本切换目标统一使用的 occurrence assemblyNodeId。
 *
 * 原始节点必须优先使用工作文档 `node_where` 的精确桥接身份；新增节点尚无该桥接时，工作文档
 * 解析器会沿用持久化、实例和结构身份兜底。仅在工作文档没有对应节点时才读取模型节点身份。
 *
 * @param input 已识别的工作文档节点、模型节点和根 Puppy 包路径。
 * @returns 可直接传给 cad-core mutation 的权威 assemblyNodeId。
 */
function resolveReferenceTargetAssemblyNodeId(input: {
  readonly documentNode: AssemblyDocumentNode | null;
  readonly modelNode: ModelAssetNode | null;
  readonly repositoryPuppyPackageFileId?: string;
}): string {
  if (input.documentNode) {
    return resolveAssemblyDocumentNodeOccurrenceAssemblyNodeId(
      input.documentNode,
      input.repositoryPuppyPackageFileId,
    );
  }
  if (input.modelNode) {
    return resolveProjectModelNodeAssemblyNodeId(input.modelNode);
  }
  throw new Error('装配分支节点目标缺少可解析的 occurrence 身份');
}

/**
 * 收集 hierarchy 查找可使用的 occurrence 身份。
 *
 * 原始节点存在当前包 `node_where` 桥接时，只允许使用桥接恢复的持久化身份；否则沿用新增节点
 * 尚未提交前的兼容候选。这样既不会遗漏离线包内嵌节点，也不会把 nodeKey 提升为持久化身份。
 *
 * @param documentNode 当前工作文档 direct child。
 * @param assemblyNodeId 已统一解析的 occurrence 身份。
 * @param repositoryPuppyPackageFileId 当前仓库 Puppy 包路径。
 * @returns hierarchy 查找使用的身份集合。
 */
function collectHierarchyOccurrenceIdentityCandidates(
  documentNode: AssemblyDocumentNode,
  assemblyNodeId: string,
  repositoryPuppyPackageFileId?: string,
): ReadonlySet<string> {
  if (repositoryPuppyPackageFileId) {
    const repositoryIdentity = resolveRepositoryPuppyNodeOccurrenceIdentity({
      nodeWhere: documentNode.nodeWhere,
      packageFileId: repositoryPuppyPackageFileId,
    });
    if (repositoryIdentity.ok) {
      return new Set([assemblyNodeId]);
    }
  }

  return new Set(collectAssemblyDocumentNodeIdentityLookupIds(documentNode));
}

/**
 * 识别左侧装配节点引用在当前装配运行态中的真实目标。
 *
 * 设计说明：
 * 1. 左侧节点 ID 只是仓库/提交/实例引用，不等同于 project-model nodeKey；
 * 2. 递归加载时优先命中 root.children，可直接局部替换；
 * 3. complete WorkAssembly 的 runtime binding 负责把工作文档 key 桥接到物化节点 canonical key；
 * 4. root `.puppy` 重新加载时 root.children 可能为空，但工作文档与 `.puppy` hierarchy
 *    仍能证明该节点已经存在，此时不能再误判为缺失并重复 append；
 * 5. 只有工作文档存在、模型树和运行期 hierarchy 都不存在时，才是创建空子节点后的补入场景。
 *
 * @param input 识别输入。
 * @returns 命中的目标；无法定位时返回 null。
 */
export function resolveAssemblyBranchNodeReferenceTarget(
  input: ResolveAssemblyBranchNodeReferenceTargetInput,
): AssemblyBranchNodeReferenceTarget | null {
  const documentNode = input.assemblyDocument
    ? resolveDocumentDirectChildByReference(input.assemblyDocument, input.rootNodeKey, input.reference)
    : null;
  const runtimeBinding = documentNode
    ? input.resolveRuntimeBindingByNodeKey?.(documentNode.nodeKey) ?? null
    : null;
  const modelNode = runtimeBinding
    // instance_id 只在各自归档包内唯一；存在绑定时必须按 canonical key 精确命中，禁止跨包同名抢占。
    ? input.root.children.find((child) => child.nodeKey === runtimeBinding.nodeKey) ?? null
    : documentNode
      ? resolveProjectModelChildByDocumentNode(input.root, documentNode)
      : resolveProjectModelChildByReference(input.root, input.reference);
  if (!documentNode && !modelNode) {
    return null;
  }

  if (modelNode) {
    const assemblyNodeId = runtimeBinding?.runtimeAssemblyNodeId
      ?? resolveReferenceTargetAssemblyNodeId({
        documentNode,
        modelNode,
        repositoryPuppyPackageFileId: input.repositoryPuppyPackageFileId,
      });
    return {
      kind: 'project-model-child',
      nodeKey: modelNode.nodeKey,
      assemblyNodeId,
      // 归档包物化节点可能没有版本引用；仓库身份应从对应工作文档节点继承。
      repositoryId: documentNode?.repositoryId.trim() || modelNode.repositoryId,
      nodeType: modelNode.nodeType,
      modelNode,
      documentNode,
      hierarchyNode: null,
      objectId: null,
      shouldAppendMissingNode: false,
    };
  }

  if (!documentNode) {
    return null;
  }

  if (runtimeBinding) {
    return {
      kind: 'runtime-embedded-child',
      // 局部项目模型事务必须使用 canonical nodeKey；工作文档 key 由 documentNode 独立保留。
      nodeKey: runtimeBinding.nodeKey,
      assemblyNodeId: runtimeBinding.runtimeAssemblyNodeId,
      repositoryId: documentNode.repositoryId,
      nodeType: documentNode.nodeType,
      modelNode: null,
      documentNode,
      hierarchyNode: null,
      objectId: runtimeBinding.componentRootObjectId,
      shouldAppendMissingNode: false,
    };
  }

  const assemblyNodeId = resolveReferenceTargetAssemblyNodeId({
    documentNode,
    modelNode: null,
    repositoryPuppyPackageFileId: input.repositoryPuppyPackageFileId,
  });

  const candidateIds = collectHierarchyOccurrenceIdentityCandidates(
    documentNode,
    assemblyNodeId,
    input.repositoryPuppyPackageFileId,
  );
  const hierarchyNode = input.hierarchy && candidateIds.size > 0
    ? findHierarchyNodeByAssemblyNodeId(input.hierarchy, candidateIds)
    : null;
  if (hierarchyNode) {
    return {
      kind: 'runtime-embedded-child',
      nodeKey: documentNode.nodeKey,
      assemblyNodeId,
      repositoryId: documentNode.repositoryId,
      nodeType: documentNode.nodeType,
      modelNode: null,
      documentNode,
      hierarchyNode,
      objectId: resolveHierarchyObjectId(hierarchyNode),
      shouldAppendMissingNode: false,
    };
  }

  return {
    kind: 'working-document-child',
    nodeKey: documentNode.nodeKey,
    assemblyNodeId,
    repositoryId: documentNode.repositoryId,
    nodeType: documentNode.nodeType,
    modelNode: null,
    documentNode,
    hierarchyNode: null,
    objectId: null,
    shouldAppendMissingNode: true,
  };
}
